import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "..";
import Cookies from 'js-cookie';
import ELocalStorageKeys from "types/LocalStorageKey";
import { TInventoryOption, inventories } from "constants/common";
import { ELocalSavedFilters, ISavedDefaultFilters, getDefaultFilters, setDefaultFilters } from "utils/defaultFilters";
import EUserType from "types/UserTypes";
import { TDealer, TDealerCRM } from "api/dealershipApi";
import { UserRolesEnum } from "enums/UserRolesEnum";
import { refreshAccessToken } from "utils/authSession";

type VerifyTokenParams = {
  email: string;
  token: string;
};

type VerifyTokenPayload = {
  body: Body;
}

type Body = {
  message: string;
}

const parseJsonSafely = async (response: Response) => {
  try {
    return await response.json();
  } catch {
    return {};
  }
};

export const verifyToken = createAsyncThunk('auth/verifyToken', async (params: VerifyTokenParams, thunkAPI) => {
  const url = `${process.env.REACT_APP_API_URL}api/v2/users/verify_token`;

  let response: Response;

  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email_address: params.email,
        token: params.token,
      }),
    });
  } catch (error) {
    // Network failure (offline, CORS, server down) - not an auth failure.
    return thunkAPI.rejectWithValue({ statusCode: 0, networkError: true });
  }

  if (!response.ok) {
    // Access token is expired/invalid: try to get a new one before giving up.
    // On success the store is updated with the new token (tokenRefreshed).
    const newAccessToken = await refreshAccessToken();

    if (newAccessToken) {
      return {
        body: { message: 'Token refreshed.' },
        statusCode: 200,
      };
    }

    return thunkAPI.rejectWithValue({
      ...await parseJsonSafely(response),
      statusCode: response.status,
    });
  }

  const data = await parseJsonSafely(response);

  return {
    ...data,
    statusCode: response.status,
  };

}, {
  // Nothing to verify when there is no session.
  condition: (params) => Boolean(params.token && params.email),
});

type LoginUserParams = {
  email: string;
  password: string;
  recaptchaToken: string | null;
};

export const loginUser = createAsyncThunk('auth/login', async (params: LoginUserParams, thunkAPI) => {
  
  const url = `${process.env.REACT_APP_API_URL}api/v2/users/login`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email_address: params.email,
      password: params.password,
      recaptcha_token: params.recaptchaToken,
    }),
  });

  if (!response.ok) {
    return thunkAPI.rejectWithValue({
      ...await response.json(),
      statusCode: response.status,
    });
  }

  const data = await response.json();

  return {
    ...data,
    statusCode: response.status,
  };

});

export const logoutUser = createAsyncThunk('auth/logout', async (_, thunkAPI) => {
  
  const url = `${process.env.REACT_APP_API_URL}api/v2/users/logout`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${Cookies.get(ELocalStorageKeys.AccessToken)}`,
    },
  });
  
  if (!response.ok) {
    return thunkAPI.rejectWithValue({
      ...await parseJsonSafely(response),
      statusCode: response.status,
    });
  }
  
  const data = await parseJsonSafely(response);

  return {
    ...data,
    statusCode: response.status,
  };

});

const defaultInventory = getDefaultFilters<
  ISavedDefaultFilters[ELocalSavedFilters.SelectedInventoryHeader]
>(ELocalSavedFilters.SelectedInventoryHeader, inventories[0]);

// Get the current URL
const url = window.location.href;

// Create a URL object
const urlObj = new URL(url);

// Get the search parameters
const searchParams = new URLSearchParams(urlObj.search);

const inventory = searchParams.get("inventory");

const inventoryParams = inventories.find((inv) => inv.value === inventory);

interface IResultsManager {
  email: string;
  name: string;
  phone: string;
  secondaryPhone: string;
}

export interface IDealer {
  name: string;
  uid: string;
  resultsManagers: IResultsManager[];
  crm: TDealerCRM| null;
}


type LoginPayload = {
  body: {
    access_token: string;
    refresh_token: string;
    jwt: {
      Username: string;
    };
    roles: UserRolesEnum;
    page_preference: {
      settings: object;
    };
  }
};

// Define a type for the slice state
export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  email: string | null;
  inventory: TInventoryOption;
  dealer: IDealer;
  type: EUserType;
  dealerV2: TDealer;
  roles: UserRolesEnum[]
}

// Define the initial state using that type
const initialState: AuthState = {
  accessToken: Cookies.get(ELocalStorageKeys.AccessToken) || null,
  refreshToken: Cookies.get(ELocalStorageKeys.RefreshToken) || null,
  email: Cookies.get(ELocalStorageKeys.UserEmail) || null,
  inventory: inventoryParams ?? defaultInventory ?? inventories[0], // inventories[1]
  dealer: {
    crm: null,
    name: "",
    uid: "",
    resultsManagers: [],
  },
  dealerV2: {
    crm: null,
    is_carfax: false,
    name: "",
    new: true,
    used: true,
    results_managers: [],
    third_party_provider: [],
    uid: "",
    is_lite: false,
    is_vauto_new: false,
    is_vauto_used: false,
  },
  type: EUserType.Admin,
  roles: Cookies.get(ELocalStorageKeys.UserRoles) ? JSON.parse(Cookies.get(ELocalStorageKeys.UserRoles)!) : [],
};

const clearAuthCookies = () => {
  Cookies.remove(ELocalStorageKeys.AccessToken);
  Cookies.remove(ELocalStorageKeys.RefreshToken);
  Cookies.remove(ELocalStorageKeys.UserEmail);
  Cookies.remove(ELocalStorageKeys.UserRoles);
};

const resetAuthState = (state: AuthState) => {
  clearAuthCookies();
  state.accessToken = null;
  state.refreshToken = null;
  state.email = null;
  state.roles = [];
  // Don't carry the previous user's dealer over to the next login.
  state.dealer = initialState.dealer;
  state.dealerV2 = initialState.dealerV2;
};

const clearClientCacheAndReload = () => {
  localStorage.clear();
  sessionStorage.clear();
  if ('caches' in window) {
    caches.keys().then((names) => names.forEach((name) => caches.delete(name)));
  }
  window.location.reload();
};

const authSlice = createSlice({
  name: "auth",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setInventory: (state, action: PayloadAction<Pick<AuthState, 'inventory'>>) => {
      state.inventory = action.payload.inventory;
    },
    setDealer: (state, action: PayloadAction<Pick<AuthState, 'dealer'>>) => {
      
      setDefaultFilters<ISavedDefaultFilters[ELocalSavedFilters.SelectedDealerHeader]>(ELocalSavedFilters.SelectedDealerHeader, action.payload.dealer);
      
      state.dealer = action.payload.dealer;
    
    },

    setDealerV2: (state, action: PayloadAction<Pick<AuthState, 'dealerV2'>>) => {
      setDefaultFilters<ISavedDefaultFilters[ELocalSavedFilters.SelectedDealerHeaderV2]>(ELocalSavedFilters.SelectedDealerHeaderV2, action.payload.dealerV2);
      state.dealerV2 = action.payload.dealerV2;
    },

    // Used when an API call returns 401 and the token could not be refreshed.
    sessionExpired: (state) => {
      resetAuthState(state);
    },

    // New access token from the refresh_token endpoint (cookie is set by
    // utils/authSession).
    tokenRefreshed: (state, action: PayloadAction<string>) => {
      state.accessToken = action.payload;
    },
    
  },
  extraReducers(builder) {
    
    builder.addCase(loginUser.fulfilled, (state, action: PayloadAction<LoginPayload>) => {
      
      Cookies.set(ELocalStorageKeys.AccessToken, action.payload.body.access_token!, { secure: true, sameSite: 'strict' }); 
      Cookies.set(ELocalStorageKeys.RefreshToken, action.payload.body.refresh_token!, { secure: true, sameSite: 'strict' }); 
      Cookies.set(ELocalStorageKeys.UserEmail, action.payload.body.jwt.Username!, { secure: true, sameSite: 'strict' }); 
      Cookies.set(ELocalStorageKeys.UserRoles, JSON.stringify(action.payload.body.roles), { secure: true, sameSite: 'strict' });

      // save the page preference settings to local storage after login
      if (action.payload.body.page_preference && action.payload.body.page_preference.settings) {
        const stringifiedFilters = JSON.stringify(action.payload.body.page_preference.settings);
        localStorage.setItem(ELocalStorageKeys.DefaultFilters, stringifiedFilters);
      } else {
        localStorage.removeItem(ELocalStorageKeys.DefaultFilters);
      }

      state.accessToken = action.payload.body.access_token;
      state.refreshToken = action.payload.body.refresh_token;
      state.email = action.payload.body.jwt.Username;
      state.roles = [action.payload.body.roles];
    });

    // Logging out must always succeed client-side. If the token is already
    // expired the backend rejects /logout (401), so handle both outcomes.
    builder.addCase(logoutUser.fulfilled, (state) => {
      resetAuthState(state);
      clearClientCacheAndReload();
    });

    builder.addCase(logoutUser.rejected, (state) => {
      resetAuthState(state);
      clearClientCacheAndReload();
    });

    builder.addCase(verifyToken.rejected, (state, action) => {
      const payload = action.payload as { networkError?: boolean } | undefined;

      // Don't log the user out because of a network hiccup.
      if (payload?.networkError) return;

      // Ignore stale responses: if the user logged in again while this
      // request was in flight, don't wipe the new session.
      if (action.meta.arg.token !== state.accessToken) return;

      resetAuthState(state);
    });

    builder.addCase(verifyToken.fulfilled, (state, action: PayloadAction<VerifyTokenPayload>) => {
      // console.log('verifyToken.fulfilled', action.payload);
    });
  },
});

export const { setInventory, setDealer, setDealerV2, sessionExpired, tokenRefreshed } = authSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectAccessToken = (state: RootState) => state.auth.accessToken;

export const selectRefreshToken = (state: RootState) => state.auth.refreshToken;

export const selectIsAuthenticated = (state: RootState) => state.auth.accessToken !== null;

export const selectUserData = (state: RootState) => state.auth;

export const selectUserRoles = (state: RootState) => state.auth.roles;

export default authSlice.reducer;
